// deps
import React, { memo, useCallback, useContext, useMemo } from "react";

// internal deps
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { AnalyticsContext, isToingFlow, useSessionStorage } from "@swiggy-private/checkout-core";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// analytics
import { SCREEN_NAME } from "@cart-client/analytics/constants";

// types
import {
    PLBillAnalytics,
    PLBillHeaderCollapsible,
    PLBillIconPrimitive,
    PLBillImagePrimitive,
} from "@swiggy-private/cart-types";
import { PL_BILL_COLLAPSIBLE_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";

// components
import { BillPrimitives, CollapsibleBillHeaderAccessory } from "@swiggy-private/cart-ui";

// styles
import {
    CollapseContent,
    CollapseContentSeparator,
    HeaderButton,
    HeaderInfoText,
    HeaderLeft,
    HeaderRow,
    HeaderTitleBlock,
    HeaderTitleRow,
    UpsellChip,
    UpsellChipIconWrapper,
    UpsellChipWrapper,
} from "./PLBillCollapsibleHeader.style";
import { isEmpty } from "@swiggy-private/js-utils";

interface PLBillCollapsibleHeaderProps {
    header: PLBillHeaderCollapsible;
    children: React.ReactNode;
}

const PLBillCollapsibleHeader = ({ header, children }: PLBillCollapsibleHeaderProps) => {
    const isToing = useMemo(() => isToingFlow(), []);

    const [isExpanded, setIsExpanded] = useSessionStorage(
        "is_bill_detail_section_uncollapsed",
        isToing || header.default_state === "expanded",
    );

    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    useEffectOnce(() => {
        if (!isEmpty(header?.analytics) && !isEmpty(header?.analytics?.impression_action)) {
            impressionEvent({
                category: header?.analytics?.screen_name ?? SCREEN_NAME.CHECKOUT,
                action: header?.analytics?.impression_action,
                context: header?.analytics?.context,
            });
        }

        const upsellAnalytics: PLBillAnalytics | undefined = header.upsell?.analytics;
        if (!isEmpty(upsellAnalytics) && !isEmpty(upsellAnalytics?.impression_action)) {
            impressionEvent({
                category: upsellAnalytics?.screen_name ?? SCREEN_NAME.CHECKOUT,
                action: upsellAnalytics?.impression_action,
                context: upsellAnalytics?.context,
            });
        }
    });

    const handleToggle = useCallback(() => {
        if (isToing) {
            return;
        }

        const nextExpanded = !isExpanded;

        if (!isEmpty(header?.analytics) && !isEmpty(header?.analytics?.click_action)) {
            clickEvent({
                category: header?.analytics?.screen_name ?? SCREEN_NAME.CHECKOUT,
                action: header?.analytics?.click_action,
                context: header?.analytics?.context,
            });
        }

        setIsExpanded(nextExpanded);
    }, [isExpanded, isToing, header, setIsExpanded, clickEvent]);

    const upsellIconItem = header.upsell?.items.find((i): i is PLBillIconPrimitive => i.type === "icon");
    const upsellTextItems = header.upsell?.items.filter((i) => i.type !== "icon");
    const getToingStamp = header.details
        .flatMap((col) => col.items)
        .find((item): item is PLBillImagePrimitive => item.type === "rightImage");
    const effectivelyExpanded = isToing || isExpanded;

    return (
        <>
            <HeaderButton
                onClick={isToing ? undefined : handleToggle}
                aria-expanded={effectivelyExpanded}
                data-testid={PL_BILL_COLLAPSIBLE_HEADER_TEST_IDS.TOGGLE_BUTTON}
            >
                <HeaderRow>
                    <HeaderLeft>
                        <HeaderTitleBlock>
                            <HeaderTitleRow>
                                {header?.details?.map((col, idx) => (
                                    <BillPrimitives key={idx} items={col.items} />
                                ))}
                            </HeaderTitleRow>
                            {!isEmpty(header?.info_text) && (
                                <HeaderInfoText>
                                    {header?.info_text?.map((t, i) => (
                                        <Text
                                            as="span"
                                            key={i}
                                            variant={t.properties?.dls?.textVariant || "V2.body3.neutral.regular"}
                                            color={t.properties?.dls?.textColor || "positive"}
                                            style={t.properties?.style as React.CSSProperties}
                                        >
                                            {t.text}
                                        </Text>
                                    ))}
                                </HeaderInfoText>
                            )}
                        </HeaderTitleBlock>
                    </HeaderLeft>

                    <CollapsibleBillHeaderAccessory
                        isToing={isToing}
                        isExpanded={effectivelyExpanded}
                        stampId={getToingStamp?.image}
                        stampWidth={getToingStamp?.properties?.style?.width}
                        stampHeight={getToingStamp?.properties?.style?.height}
                    />
                </HeaderRow>

                {!isEmpty(header?.upsell) && (
                    <UpsellChipWrapper isExpanded={effectivelyExpanded}>
                        <UpsellChip
                            isExpanded={effectivelyExpanded}
                            data-testid={PL_BILL_COLLAPSIBLE_HEADER_TEST_IDS.UPSELL_CHIP}
                        >
                            {!isEmpty(upsellIconItem) && (
                                <UpsellChipIconWrapper>
                                    <Image cloudinaryId={upsellIconItem.icon} alt="" width="20px" height="20px" />
                                </UpsellChipIconWrapper>
                            )}
                            {!isEmpty(upsellTextItems) && <BillPrimitives items={upsellTextItems} />}
                        </UpsellChip>
                    </UpsellChipWrapper>
                )}
            </HeaderButton>

            <CollapseContent
                isExpanded={effectivelyExpanded}
                data-testid={PL_BILL_COLLAPSIBLE_HEADER_TEST_IDS.COLLAPSE_CONTENT}
            >
                <CollapseContentSeparator />
                {children}
            </CollapseContent>
        </>
    );
};

export default memo(PLBillCollapsibleHeader);
